import { cloneElement, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, ComposedChart,
  PieChart, Pie, Cell, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  RadialBarChart, RadialBar, PolarAngleAxis,
} from "recharts";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import {
  aggregate, formatValue, groupBy, toNumber,
  type ColumnProfile, type TileSpec,
} from "@/lib/dashboard/engine";

export const PALETTE = [
  "hsl(var(--primary))", "hsl(var(--accent))", "hsl(var(--chart-3, 32 90% 55%))",
  "hsl(var(--chart-4, 280 65% 60%))", "hsl(var(--chart-5, 160 60% 45%))",
  "hsl(var(--chart-6, 350 70% 55%))", "hsl(var(--chart-7, 200 70% 50%))", "hsl(var(--muted-foreground))",
];

const axisTick = { fontSize: 11, fill: "hsl(var(--muted-foreground))" };
const tooltipStyle = {
  contentStyle: {
    background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))",
    borderRadius: 8, color: "hsl(var(--popover-foreground))", fontSize: 12,
  },
};

interface Props {
  spec: TileSpec;
  rows: Record<string, any>[];
  previousRows?: Record<string, any>[] | null;
  profiles: ColumnProfile[];
  selected?: string[];
  onSelect?: (value: string) => void;
}

export function StudioChart({ spec, rows, previousRows, profiles, selected = [], onSelect }: Props) {
  const { points, seriesKeys } = useMemo(() => groupBy(rows, spec, profiles), [rows, spec, profiles]);
  const fmt = (n: number) => formatValue(n, spec.measure);
  const dim = (name: string) => selected.length > 0 && !selected.includes(name);
  const click = (d: any) => {
    const name = d?.activeLabel ?? d?.name ?? d?.payload?.name;
    if (name != null && onSelect) onSelect(String(name));
  };

  if (!rows.length) {
    return <Empty text="No rows match the current filters." />;
  }

  if (spec.type === "kpi") {
    const val = aggregate(spec.measure ? rows.map((r) => r[spec.measure!]) : rows, spec.measure ? spec.aggregation : "count");
    const prev = previousRows
      ? aggregate(spec.measure ? previousRows.map((r) => r[spec.measure!]) : previousRows, spec.measure ? spec.aggregation : "count")
      : null;
    const change = prev && val !== null ? ((val - prev) / Math.abs(prev)) * 100 : null;
    const Icon = change === null ? Minus : change >= 0 ? ArrowUpRight : ArrowDownRight;
    return (
      <div className="flex h-full flex-col justify-center gap-1">
        <div className="font-display text-3xl font-semibold tracking-tight">{formatValue(val, spec.measure, false)}</div>
        <div className={`flex items-center gap-1 text-xs ${change === null ? "text-muted-foreground" : change >= 0 ? "text-accent" : "text-destructive"}`}>
          <Icon className="h-3.5 w-3.5" />
          {change === null ? "Set a date range to compare periods" : `${change >= 0 ? "+" : ""}${change.toFixed(1)}% vs previous period`}
        </div>
      </div>
    );
  }

  if (spec.type === "table") {
    const cols = profiles.slice(0, 8).map((p) => p.name);
    const numCols = profiles.filter((p) => p.type === "number").map((p) => p.name);
    const max: Record<string, number> = {};
    numCols.forEach((c) => (max[c] = Math.max(...rows.map((r) => Math.abs(toNumber(r[c]) ?? 0)), 1)));
    return (
      <div className="h-full overflow-auto text-xs">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 bg-card">
            <tr>{cols.map((c) => <th key={c} className="border-b px-2 py-1.5 text-left font-medium text-muted-foreground">{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.slice(0, 200).map((r, i) => (
              <tr key={i} className="border-b border-border/50">
                {cols.map((c) => {
                  const n = numCols.includes(c) ? toNumber(r[c]) : null;
                  return (
                    <td key={c} className="relative px-2 py-1 tabular-nums">
                      {n !== null && <span className="absolute inset-y-1 left-0 rounded-sm bg-primary/10" style={{ width: `${(Math.abs(n) / max[c]) * 100}%` }} />}
                      <span className="relative">{String(r[c] ?? "")}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 200 && <p className="p-2 text-muted-foreground">Showing 200 of {rows.length.toLocaleString()} rows.</p>}
      </div>
    );
  }

  if (spec.type === "heatmap") {
    if (!seriesKeys.length) return <Empty text="Pick a column for the heatmap columns." />;
    const all = points.flatMap((p) => seriesKeys.map((s) => p[s] as number));
    const hi = Math.max(...all, 1);
    return (
      <div className="h-full overflow-auto text-xs">
        <table className="w-full border-separate border-spacing-0.5">
          <thead><tr><th />{seriesKeys.map((s) => <th key={s} className="px-1 py-1 font-medium text-muted-foreground">{s}</th>)}</tr></thead>
          <tbody>
            {points.map((p) => (
              <tr key={p.name}>
                <td className="whitespace-nowrap pr-2 text-muted-foreground">{p.name}</td>
                {seriesKeys.map((s) => (
                  <td key={s} className="cursor-pointer rounded px-1 py-1.5 text-center tabular-nums"
                    style={{ background: `hsl(var(--primary) / ${0.08 + 0.85 * ((p[s] as number) / hi)})` }}
                    onClick={() => onSelect?.(p.name)}>
                    {fmt(p[s] as number)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (spec.type === "gauge") {
    const val = aggregate(spec.measure ? rows.map((r) => r[spec.measure!]) : rows, spec.measure ? spec.aggregation : "count") ?? 0;
    const target = spec.target && spec.target > 0 ? spec.target : null;
    if (!target) return <Empty text="Set a target value in the tile settings." />;
    const pct = Math.min(100, (val / target) * 100);
    return (
      <div className="relative h-full">
        <Fit>
          <RadialBarChart innerRadius="70%" outerRadius="100%" data={[{ value: pct }]} startAngle={210} endAngle={-30}>
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <RadialBar dataKey="value" cornerRadius={8} fill="hsl(var(--primary))" background={{ fill: "hsl(var(--muted))" }} />
          </RadialBarChart>
        </Fit>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display text-2xl font-semibold">{pct.toFixed(0)}%</span>
          <span className="text-xs text-muted-foreground">{fmt(val)} of {fmt(target)}</span>
        </div>
      </div>
    );
  }

  if (spec.type === "scatter") {
    if (!spec.measure || !spec.measure2) return <Empty text="Pick two number columns." />;
    const pts = rows.slice(0, 2000).map((r) => ({ x: toNumber(r[spec.measure!]), y: toNumber(r[spec.measure2!]) }))
      .filter((p) => p.x !== null && p.y !== null);
    return (
      <Fit>
        <ScatterChart margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="x" type="number" name={spec.measure} tick={axisTick} tickFormatter={(v) => formatValue(v)} />
          <YAxis dataKey="y" type="number" name={spec.measure2} tick={axisTick} tickFormatter={(v) => formatValue(v)} width={48} />
          <Tooltip {...tooltipStyle} cursor={{ strokeDasharray: "3 3" }} />
          <Scatter data={pts} fill="hsl(var(--primary))" fillOpacity={0.6} />
        </ScatterChart>
      </Fit>
    );
  }

  if (!points.length) return <Empty text="Pick a column to group by." />;

  if (spec.type === "pie") {
    return (
      <Fit>
        <PieChart>
          <Pie data={points} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="85%" paddingAngle={2} onClick={click}>
            {points.map((p, i) => <Cell key={p.name} fill={PALETTE[i % PALETTE.length]} opacity={dim(p.name) ? 0.3 : 1} className="cursor-pointer" />)}
          </Pie>
          <Tooltip {...tooltipStyle} formatter={(v: number) => fmt(v)} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </Fit>
    );
  }

  if (spec.type === "funnel") {
    const top = Math.max(...points.map((p) => p.value), 1);
    return (
      <div className="flex h-full flex-col justify-center gap-1.5">
        {points.map((p, i) => (
          <button key={p.name} onClick={() => onSelect?.(p.name)} className="group flex items-center gap-2 text-left text-xs">
            <span className="w-24 truncate text-muted-foreground">{p.name}</span>
            <span className="relative h-6 flex-1">
              <span className="absolute inset-y-0 left-1/2 -translate-x-1/2 rounded transition-opacity"
                style={{ width: `${(p.value / top) * 100}%`, background: PALETTE[i % PALETTE.length], opacity: dim(p.name) ? 0.3 : 1 }} />
            </span>
            <span className="w-14 text-right tabular-nums">{fmt(p.value)}</span>
          </button>
        ))}
      </div>
    );
  }

  const common = { data: points, margin: { top: 8, right: 12, bottom: 4, left: 0 }, onClick: click };
  const xAxis = <XAxis dataKey="name" tick={axisTick} interval="preserveStartEnd" minTickGap={12} />;
  const yAxis = <YAxis tick={axisTick} tickFormatter={(v) => formatValue(v)} width={48} />;
  const grid = <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />;
  const tip = <Tooltip {...tooltipStyle} formatter={(v: number) => fmt(v)} cursor={{ fill: "hsl(var(--muted) / 0.5)" }} />;

  if (spec.type === "stacked") {
    const keys = seriesKeys.length ? seriesKeys : ["value"];
    return (
      <Fit>
        <BarChart {...common}>
          {grid}{xAxis}{yAxis}{tip}<Legend wrapperStyle={{ fontSize: 11 }} />
          {keys.map((k, i) => <Bar key={k} dataKey={k} stackId="s" fill={PALETTE[i % PALETTE.length]} className="cursor-pointer" />)}
        </BarChart>
      </Fit>
    );
  }
  if (spec.type === "line") {
    return (
      <Fit>
        <LineChart {...common}>{grid}{xAxis}{yAxis}{tip}
          <Line type="monotone" dataKey="value" name={spec.measure ?? "Records"} stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
        </LineChart>
      </Fit>
    );
  }
  if (spec.type === "area") {
    return (
      <Fit>
        <AreaChart {...common}>{grid}{xAxis}{yAxis}{tip}
          <Area type="monotone" dataKey="value" name={spec.measure ?? "Records"} stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.15} strokeWidth={2} />
        </AreaChart>
      </Fit>
    );
  }
  if (spec.type === "combo") {
    return (
      <Fit>
        <ComposedChart {...common}>{grid}{xAxis}{yAxis}
          {spec.measure2 && <YAxis yAxisId="r" orientation="right" tick={axisTick} tickFormatter={(v) => formatValue(v)} width={48} />}
          {tip}<Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="value" name={spec.measure ?? "Records"} fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
          {spec.measure2 && <Line yAxisId="r" type="monotone" dataKey="value2" name={spec.measure2} stroke="hsl(var(--accent))" strokeWidth={2} dot={false} />}
        </ComposedChart>
      </Fit>
    );
  }
  // bar
  return (
    <Fit>
      <BarChart {...common}>{grid}{xAxis}{yAxis}{tip}
        <Bar dataKey="value" name={spec.measure ?? "Records"} radius={[4, 4, 0, 0]} className="cursor-pointer">
          {points.map((p) => <Cell key={p.name} fill="hsl(var(--primary))" opacity={dim(p.name) ? 0.3 : 1} />)}
        </Bar>
      </BarChart>
    </Fit>
  );
}

const Empty = ({ text }: { text: string }) => (
  <div className="flex h-full items-center justify-center p-4 text-center text-sm text-muted-foreground">{text}</div>
);

/** Measures its box and passes explicit width/height to the chart (more reliable than ResponsiveContainer inside grid cards). */
function Fit({ children }: { children: ReactElement }) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className="h-full w-full min-w-0">
      {size.w > 0 && size.h > 0 && cloneElement(children, { width: size.w, height: size.h })}
    </div>
  );
}
